import { useMemo, useState } from 'react'

import { EnhancedDimension, useUnsafeEffect } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import { EmployeeForSelectFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import {
  EmployeeForSelectFragmentNoAccess,
  Groups,
} from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { GroupType } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import type { FilterProps } from 'modules/core/components/Filters/types'
import EmployeeSelectorInput from 'modules/core/components/Inputs/EmployeeSelector'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import employees from 'modules/employees/store/collections/employees'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'

export type IdsTypes = number[] | (number | null | undefined)

interface Props {
  field: Field
  hideLabel?: boolean
  label?: string
  onSelectEmployee: (ids: IdsTypes) => void
  selectedEmployeeIds: number[]
  selectableEmployeeIds: number[]
  error?: string
  filterProps?: FilterProps
  alwaysOpen?: boolean
  width?: EnhancedDimension | null
  defaultGroup?: GroupType
}

const EmployeesSelector = ({
  field,
  hideLabel,
  label,
  onSelectEmployee,
  selectedEmployeeIds,
  selectableEmployeeIds,
  filterProps,
  alwaysOpen = false,
  width = null,
  defaultGroup = Groups.ALL,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEmployeesSelector = hasFeatureFlag('dev_shift_management_new_employees_selector')
  const initialSelectedEmployees: EmployeeForSelectFragment[] = useMemo(
    () =>
      selectedEmployeeIds.map((id) => ({
        id,
        firstName: '',
        fullName: `1 ${i18n.t('employee_selector.selected')}`,
        lastName: '',
        access: {
          id: 0,
        },
      })),
    [selectedEmployeeIds]
  )

  const [selectedEmployees, setSelectedEmployees] =
    useState<EmployeeForSelectFragmentNoAccess[]>(initialSelectedEmployees)

  const handleOnSelect = (employeesList: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(employeesList)
    onSelectEmployee(employeesList.map((employee) => employee.id))
  }

  useUnsafeEffect(() => {
    if (selectedEmployees.length != 0 || !newEmployeesSelector) return
    // Pre-select the employees when the component is mounted
    setSelectedEmployees(initialSelectedEmployees)
  }, [initialSelectedEmployees, selectedEmployees, newEmployeesSelector])

  if (hasFeatureFlag('dev_shift_management_new_employees_selector')) {
    return (
      <EmployeeSelector
        alwaysOpen={alwaysOpen}
        label={hideLabel ? '' : label || i18n.t('shift_management.no_selection_form.assign_to')}
        value={selectedEmployees}
        onSelect={handleOnSelect}
        hideSearchIcon
        withAvatar
        multiple
        width={width ?? undefined}
        allowedGroups={['all', 'teams', 'managers', 'workplaces']}
        defaultGroup={defaultGroup}
        // TODO: Replace this with the selectedEmployeeIds without Employees mobx collection.
        employeeIds={selectableEmployeeIds}
      />
    )
  }

  // Explore how to manually force the fetch here instead of doing it in the bootstrap.
  const selectableEmployees = employees.filter((employee) =>
    selectableEmployeeIds.includes(employee.id)
  )

  return (
    <EmployeeSelectorInput
      hasSubmitButton
      isMultiSelect
      attachment="top center"
      field={field}
      label={hideLabel ? '' : label || i18n.t('shift_management.no_selection_form.assign_to')}
      employees={selectableEmployees}
      onSelectEmployee={onSelectEmployee}
      selectedEmployeeIds={selectedEmployeeIds}
      virtualized
      multiFilters
      width={width}
      filterProps={filterProps}
    />
  )
}

export default observer(EmployeesSelector)
